import { Warehouse as WarehouseIcon, CaretUpDown } from "@phosphor-icons/react"
import { trackEvent } from "~/hooks/useTrackingService"
import { usePrimaryWarehouses } from "~/hooks/useWarehouses"
import { useSkuStore } from "~/providers/sku-store-provider"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/reusable/Popover"
import { Button } from "~/components/design-system/components"
import { cn } from "~/utils/helpers"
import { useMemo, useState } from "react"

type WarehouseView = {
  id: string
  name: string
  isPrimary: boolean
  onlyPreOrders: boolean
}

/**
 * `value` / `onChange` put the select in CONTROLLED mode, where the caller owns the selection instead
 * of the sku store. Passing neither keeps every existing page on the store, which is why `controlled`
 * tests `onChange` rather than `value` — an undefined `value` is a real selection, meaning "Global".
 *
 * `warehouseIds` narrows the list to a subset — a fashion buy is scoped to the warehouses it was
 * created for (PRO-1126), and offering the rest would let a planner filter to a warehouse the buy
 * does not cover and read an empty grid. Omitted everywhere else, which keeps every other page on
 * the full set.
 *
 * The two are independent: a controlled select can also be narrowed, and neither is required.
 */
export default function GlobalWarehouseSelect({
  value,
  onChange,
  warehouseIds,
}: {
  value?: string | undefined
  onChange?: (warehouseId: string | undefined) => void
  warehouseIds?: readonly string[]
} = {}) {
  const updateWarehouseId = useSkuStore((state) => state.updateWarehouseId)
  const storeWarehouseId = useSkuStore((state) => state.warehouseId)
  const controlled = onChange !== undefined
  const selectedWarehouseId = controlled ? value : storeWarehouseId

  const allWarehouses = usePrimaryWarehouses()
  const warehouses = useMemo(
    () =>
      warehouseIds
        ? allWarehouses.filter((w: WarehouseView) => warehouseIds.includes(w.id))
        : allWarehouses,
    [allWarehouses, warehouseIds],
  )
  const showWarehouses = warehouses.length > 1

  const [open, setOpen] = useState(false)

  // Controlled mode hands the id to the caller and does NOT track: the store selection is a global
  // navigation choice worth an event, while a caller-owned one is a local filter.
  const setWarehouse = (id: string | undefined, name: string) => {
    if (controlled) onChange(id)
    else {
      updateWarehouseId(id)
      trackEvent("Warehouse selected", { Warehouse: name })
    }
    setOpen(false)
  }

  // A selection outside the offered set reads as "Global", matching what the page actually shows.
  const selectedName = warehouses.find((w: WarehouseView) => w.id === selectedWarehouseId)?.name

  const trigger = (
    <Button size="sm" variant="neutral">
      <WarehouseIcon />
      {selectedName ?? "All warehouses"}
      <CaretUpDown />
    </Button>
  )

  return showWarehouses ? (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-fit rounded-[12px] p-0 overflow-hidden divide-y divide-border-base"
        align="start"
        sideOffset={8}
      >
        <div className="flex flex-col max-h-[200px] overflow-y-auto *:transition-colors">
          <div
            className={cn(
              "px-3 py-2 text-sm cursor-pointer hover:bg-background-highlight-hover",
              !selectedWarehouseId && "bg-background-highlight",
            )}
            onClick={() => setWarehouse(undefined, "Global")}
          >
            Global
          </div>
          {warehouses?.map((warehouse: WarehouseView) => (
            <div
              key={warehouse.id}
              className={cn(
                "px-3 py-2 text-sm cursor-pointer hover:bg-background-highlight-hover flex items-center gap-2",
                selectedWarehouseId === warehouse.id && "bg-background-highlight",
              )}
              onClick={() => setWarehouse(warehouse.id, warehouse.name)}
            >
              {warehouse.name}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  ) : null
}
